import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errMsg } from "@/lib/proApi";

const EMPTY = { code: "", percent: 10, pro: true, packs: true, ends_at: "", max_uses: "", note: "" };
const inp = "rounded-lg border border-[var(--oz-border)] px-3 py-2 text-sm bg-white";
const SCOPE = { pro: "Espace Pro", packs: "Packs particuliers" };

const payload = (f) => ({
  code: f.code, percent: Number(f.percent), scope: ["pro", "packs"].filter((k) => f[k]),
  ends_at: f.ends_at ? new Date(`${f.ends_at}T23:59:59`).toISOString() : null,
  max_uses: f.max_uses ? Number(f.max_uses) : null, active: f.active ?? true, note: f.note || null,
});

const CreateForm = ({ onDone }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await adminApi.post("/admin/promo-codes", payload(f)); toast.success(`Code ${f.code.toUpperCase()} créé`); setF(EMPTY); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="promo-create-form" className="rounded-2xl bg-white border border-[var(--oz-border)] p-5 space-y-3">
      <h2 className="font-semibold">Nouveau code promo</h2>
      <div className="grid sm:grid-cols-4 gap-2">
        <input required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, "") })} placeholder="CODE (ex. NOEL20)" data-testid="promo-code-input" className={`${inp} sm:col-span-2 font-mono`} />
        <label className="flex items-center gap-2 text-sm"><input type="number" min="1" max="90" required value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} data-testid="promo-percent-input" className={`${inp} w-20`} /> % de remise</label>
        <input type="number" min="1" value={f.max_uses} onChange={(e) => setF({ ...f, max_uses: e.target.value })} placeholder="Max. utilisations" title="Vide = illimité" data-testid="promo-max-input" className={inp} />
      </div>
      <div className="flex flex-wrap gap-4 items-center text-sm">
        {Object.entries(SCOPE).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2"><input type="checkbox" checked={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.checked })} data-testid={`promo-scope-${k}`} className="w-4 h-4 accent-[var(--oz-gold)]" /> {l}</label>
        ))}
        <label className="flex items-center gap-2">Fin le <input type="date" lang="fr" value={f.ends_at} onChange={(e) => setF({ ...f, ends_at: e.target.value })} data-testid="promo-end-input" className={inp} /></label>
        <input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Note (facultatif)" data-testid="promo-note-input" className={`${inp} flex-1 min-w-[160px]`} />
      </div>
      <button disabled={busy || (!f.pro && !f.packs)} data-testid="promo-create-btn" className="inline-flex items-center gap-2 rounded-full bg-[var(--oz-dark)] text-white px-5 py-2.5 text-sm font-semibold disabled:opacity-50"><Plus className="w-4 h-4" /> Créer le code</button>
    </form>
  );
};

const Row = ({ c, reload }) => {
  const expired = c.ends_at && new Date(c.ends_at) < new Date();
  const full = c.max_uses && c.uses >= c.max_uses;
  const toggle = () => adminApi.put(`/admin/promo-codes/${c.code}`, payload({ ...c, pro: c.scope.includes("pro"), packs: c.scope.includes("packs"), ends_at: c.ends_at?.slice(0, 10), active: !c.active }))
    .then(reload).catch((e) => toast.error(errMsg(e)));
  const remove = () => window.confirm(`Supprimer le code ${c.code} ?`) && adminApi.delete(`/admin/promo-codes/${c.code}`).then(reload).catch((e) => toast.error(errMsg(e)));
  return (
    <li data-testid={`promo-row-${c.code}`} className="py-3 flex flex-wrap items-center gap-3 justify-between">
      <div>
        <p className="font-mono font-semibold">{c.code} <span className="font-sans text-[var(--oz-gold)]">-{c.percent} %</span></p>
        <p className="text-xs text-[var(--oz-muted)]">{c.scope.map((s) => SCOPE[s]).join(" + ")} · {c.uses} utilisation(s){c.max_uses ? ` / ${c.max_uses}` : ""}{c.ends_at ? ` · jusqu'au ${new Date(c.ends_at).toLocaleDateString("fr-FR")}` : ""}{c.note ? ` · ${c.note}` : ""}</p>
      </div>
      <div className="flex items-center gap-2">
        <span data-testid={`promo-status-${c.code}`} className={`text-xs rounded-full px-2 py-1 ${c.active && !expired && !full ? "bg-emerald-50 text-emerald-700" : "bg-neutral-100 text-neutral-600"}`}>
          {!c.active ? "Désactivé" : expired ? "Expiré" : full ? "Épuisé" : "Actif"}</span>
        <button onClick={toggle} data-testid={`promo-toggle-${c.code}`} className="text-xs rounded-full border border-[var(--oz-border)] px-3 py-1.5">{c.active ? "Désactiver" : "Activer"}</button>
        <button onClick={remove} data-testid={`promo-delete-${c.code}`} className="p-2 text-red-600"><Trash2 className="w-4 h-4" /></button>
      </div>
    </li>
  );
};

export default function AdminPromoCodes() {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => adminApi.get("/admin/promo-codes").then(({ data }) => setRows(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);
  return (
    <div data-testid="admin-promo-codes" className="max-w-4xl mx-auto px-4 sm:px-8 py-10 space-y-6">
      <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-[var(--oz-muted)]" data-testid="promo-back"><ArrowLeft className="w-4 h-4" /> Administration</Link>
      <div><h1 className="font-serif text-3xl sm:text-4xl">Codes promo</h1>
        <p className="mt-1 text-sm text-[var(--oz-muted)]">Remise en % sur les produits (hors livraison), un seul code par commande. Les codes personnels -15 % des pros restent gérés à part.</p></div>
      <CreateForm onDone={load} />
      <section className="rounded-2xl bg-white border border-[var(--oz-border)] p-5">
        <h2 className="font-semibold">Codes existants</h2>
        {rows && !rows.length && <p className="mt-2 text-sm text-[var(--oz-muted)]">Aucun code pour l'instant.</p>}
        <ul className="divide-y divide-[var(--oz-border)]" data-testid="promo-list">{rows?.map((c) => <Row key={c.code} c={c} reload={load} />)}</ul>
      </section>
    </div>
  );
}
